import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import Send from "~icons/material-symbols/send-outline";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { formatTimestamp } from "../../../libs/date";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { getSortedBalancesWithFundSources } from "../../../libs/get-sorted-balances-with-fund-sources";
import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";

export const Route = createFileRoute("/_authed/balances/")({
  loader: async ({ context: { client } }) => {
    const balanceResponse = await client.getMyBalances({}).catch((error) => {
      throw new AppError(
        ERROR_CODES.CONNECT_GET_MY_BALANCES_FAILED,
        error.message,
        {
          originalError: error,
        },
      );
    });

    const { sortedBalances } = await getSortedBalancesWithFundSources(
      client,
      balanceResponse.balanceWithFundSources,
    );

    return {
      balances: sortedBalances,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { balances } = Route.useLoaderData();
  const { tenant } = Route.useRouteContext();
  const navigate = useNavigate();
  const canTransfer = tenant.feature.transfer;

  return (
    <>
      <Header
        title={t("ポイント内訳")}
        onClickBack={() => navigate({ to: "/" })}
      />
      <div
        className={css({
          padding: "16px",
          pb: "80px",
          display: "flex",
          flexDir: "column",
          gap: "12px",
          backgroundColor: "background.background",
          minHeight: "calc(100vh - 56px)",
        })}
      >
        {balances.length === 0 && (
          <div
            className={css({
              fontSize: "16px",
              textAlign: "center",
              fontWeight: "bold",
              color: "text.secondary",
              mb: "32px",
              mt: "42px",
            })}
          >
            {t("ポイントがありません。")}
          </div>
        )}
        {balances.map((balance) => {
          const fundSourceId = balance.fundSource?.id ?? "";
          const isForbidTransfer = Boolean(balance.fundSource?.forbidTransfer);

          return (
            <div
              key={fundSourceId}
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                py: "16px",
                px: "1.25rem",
                borderRadius: "16px",
                backgroundColor: "surface.primary",
              })}
            >
              <div
                className={css({
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                })}
              >
                <span
                  className={css({
                    flex: 1,
                    minWidth: "0",
                    fontWeight: "600",
                    color: "text.primary",
                    fontSize: "16px",
                    overflowWrap: "anywhere",
                  })}
                >
                  {balance.fundSource?.name}
                </span>
                {canTransfer && !isForbidTransfer && (
                  <button
                    onClick={() =>
                      navigate({
                        to: "/transfer/input/$fundSourceId/$amount",
                        params: {
                          fundSourceId,
                          amount: "0",
                        },
                        search: {
                          from: "balances",
                        },
                      })
                    }
                    type="button"
                    className={css({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      backgroundColor: "slate.50",
                      border: "1px solid",
                      borderColor: "rgba(209, 213, 219, 0.25)",
                      borderRadius: "8px",
                      px: "12px",
                      py: "8px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "text.primary",
                      cursor: "pointer",
                      boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.03)",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    })}
                  >
                    <Send
                      className={css({
                        width: "16px",
                        height: "16px",
                      })}
                    />
                    {t("送る")}
                  </button>
                )}
              </div>
              {isForbidTransfer && (
                <p
                  className={css({
                    fontSize: "12px",
                    color: "#4b556380",
                  })}
                >
                  {t("このポイントは譲渡できません。")}
                </p>
              )}
              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                })}
              >
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  })}
                >
                  <span
                    className={css({
                      fontSize: "14px",
                      color: "text.secondary",
                    })}
                  >
                    {t("ポイント数")}
                  </span>
                  <span
                    className={css({
                      fontSize: "16px",
                      color: "text.secondary",
                    })}
                  >
                    {formatNumber(balance.userBalance?.amount) || 0}pt
                  </span>
                </div>
                {balance.userBalance?.expireAt && (
                  <div
                    className={css({
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    })}
                  >
                    <span
                      className={css({
                        fontSize: "14px",
                        color: "text.secondary",
                      })}
                    >
                      {balance.expirationDates.length > 1 ? (
                        <>{t("最短有効期限:")}</>
                      ) : (
                        <>{t("有効期限:")}</>
                      )}
                    </span>
                    <span
                      className={css({
                        fontSize: "14px",
                        color: "text.secondary",
                      })}
                    >
                      {formatTimestamp(balance.userBalance.expireAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
